import { useEffect, useRef, useState } from "react";
import { HOLD_OPTION, type Tone } from "../email";
import { firstName, pcBand, repeatMarker } from "../format";
import { Portrait } from "../portrait/Portrait";
import type { EventView, OptionView } from "../types";
import { CostPreview } from "./CostPreview";
import { Parts, PersonRef } from "./PersonRef";
import { TonePicker } from "./Reply";

function speakerName(ev: EventView): string {
  return ev.bringer?.name ?? (ev.channel === "door" ? "Someone" : "A message");
}

/** The bringer's name, linked to their card when there is one. */
function Speaker({ ev }: { ev: EventView }) {
  return ev.bringer ? <PersonRef personId={ev.bringer.id} person={ev.bringer}>{ev.bringer.name}</PersonRef> : <>{speakerName(ev)}</>;
}

function RepeatLine({ ev }: { ev: EventView }) {
  if (!ev.repeat) return null;
  const who = ev.bringer ? `${firstName(ev.bringer.name)}: ` : "";
  return <p className="repeat-marker">{who}{ev.title.replace(/^"|"$/g, "")} ({repeatMarker(ev.repeat.count)})</p>;
}

function channelNote(channel: string): string | null {
  switch (channel) {
    case "door": return null;
    case "email": return "By email";
    case "phone": return "On the phone";
    case "text": return "By text";
    case "note": return "A note on your desk";
    default: return channel.replace(/_/g, " ");
  }
}

/**
 * An urgent interruption asking for a protected block (21 §9). Said in words: answering breaks the
 * block; keeping it costs political capital. Neither is marked as the right call.
 */
export function RaidBanner({ ev }: { ev: EventView }) {
  if (!ev.raid) return null;
  return (
    <div className="raid-banner" role="note">
      <span className="raid-banner__lock" aria-hidden="true">🔒</span>
      <div>
        <p className="raid-banner__title">This is your protected time: {ev.raid.label}</p>
        <p className="raid-banner__body">Answering now breaks the block. You can keep the block and let this wait, at a political cost.</p>
      </div>
    </div>
  );
}

/** The person at the door with their opening line (19 §2.2). */
export function Door({ ev, onSee }: { ev: EventView; onSee: () => void }) {
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => { btn.current?.focus({ preventScroll: true }); }, [ev.instanceId]);
  const note = channelNote(ev.channel);
  return (
    <section className="door door--occupied" aria-labelledby="door-title" key={ev.instanceId}>
      <div className="door__frame">
        {ev.bringer && (
          <Portrait portraitId={ev.bringer.portraitId} seed={ev.bringer.id} name={ev.bringer.name} role={ev.bringer.role}
            grade={ev.bringer.grade} expression={ev.bringer.expression} strain={ev.bringer.strain} size={168} className="door__portrait" />
        )}
      </div>
      <div className="door__speech">
        {note && <p className="door__channel">{note}</p>}
        <h2 id="door-title" className="door__title">{ev.title}</h2>
        <p className="door__who"><Speaker ev={ev} />{ev.bringer ? <span className="door__role">, {ev.bringer.role}</span> : null}</p>
        <blockquote className="dialogue"><Parts parts={ev.textParts} text={ev.text} /></blockquote>
        <RepeatLine ev={ev} />
        <RaidBanner ev={ev} />
        <div className="door__actions">
          <button ref={btn} type="button" className="btn btn--primary" onClick={onSee}>
            {ev.bringer ? `See ${firstName(ev.bringer.name)}` : "Deal with it"}
          </button>
        </div>
      </div>
    </section>
  );
}

/** Keeping a protected block: no time cost, a political one (shown as a band, never a number). */
function HoldCost({ pc }: { pc: number }) {
  const band = pcBand(pc);
  if (!band) return null;
  return (
    <span className="cost">
      <span className="visually-hidden">{band === "significant" ? "Significant" : "Some"} political cost.</span>
      <span className="cost__visible" aria-hidden="true">
        <span className="cost__pc"><span className="cost__glyph">{band === "significant" ? "◑" : "◔"}</span> {band} political</span>
      </span>
    </span>
  );
}

/** The main decision surface (19 §4.2): portrait, line, options with known costs only. */
export function Conversation({ ev, busy, shortcuts, onChoose, onBack }: {
  ev: EventView; busy: boolean; shortcuts: boolean; onChoose: (optionId: string, tone?: Tone) => void; onBack: () => void;
}) {
  const first = useRef<HTMLButtonElement>(null);
  const [writing, setWriting] = useState<OptionView | null>(null);
  useEffect(() => { setWriting(null); }, [ev.instanceId]);
  useEffect(() => { if (!writing) first.current?.focus({ preventScroll: true }); }, [ev.instanceId, writing]);
  const pick = (o: OptionView) => { if (o.tones?.length) setWriting(o); else onChoose(o.id); };
  const choices: ({ kind: "option"; o: OptionView } | { kind: "hold" })[] = [
    ...ev.options.map((o) => ({ kind: "option" as const, o })),
    ...(ev.raid ? [{ kind: "hold" as const }] : []),
  ];

  // Number keys choose a response (19 §6.4). While a tone is being chosen, the picker owns them.
  useEffect(() => {
    if (!shortcuts || writing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || busy || !/^[1-9]$/.test(e.key)) return;
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      if (document.querySelector("dialog[open]")) return;
      const c = choices[Number(e.key) - 1];
      if (!c) return;
      e.preventDefault();
      if (c.kind === "hold") onChoose(HOLD_OPTION);
      else pick(c.o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <section className="conversation" aria-labelledby="conv-title">
      <div className="conversation__side">
        {ev.bringer && (
          <Portrait portraitId={ev.bringer.portraitId} seed={ev.bringer.id} name={ev.bringer.name} role={ev.bringer.role}
            grade={ev.bringer.grade} expression={ev.bringer.expression} strain={ev.bringer.strain} size={220} className="conversation__portrait" />
        )}
        <p className="conversation__name"><Speaker ev={ev} /></p>
        {ev.bringer && <p className="conversation__role">{ev.bringer.role}</p>}
      </div>
      <div className="conversation__main">
        <h2 id="conv-title" className="conversation__title">{ev.title}</h2>
        <p className="dialogue dialogue--line">
          {ev.bringer && <span className="dialogue__speaker">{firstName(ev.bringer.name)}: </span>}
          “<Parts parts={ev.textParts} text={ev.text} />”
        </p>
        <RepeatLine ev={ev} />
        <RaidBanner ev={ev} />
        {writing ? (
          <TonePicker option={writing} busy={busy} shortcuts={shortcuts} sendLabel="Say it"
            onSend={(tone) => onChoose(writing.id, tone)} onBack={() => setWriting(null)} />
        ) : (
          <>
            <h3 className="conversation__prompt" id="conv-prompt">What do you do?</h3>
            <ol className="options" aria-labelledby="conv-prompt">
              {choices.map((c, i) => (
                <li key={c.kind === "hold" ? HOLD_OPTION : c.o.id}>
                  {c.kind === "hold" ? (
                    <button ref={i === 0 ? first : undefined} type="button" className="option option--hold" disabled={busy}
                      onClick={() => onChoose(HOLD_OPTION)} aria-keyshortcuts={shortcuts && i < 9 ? String(i + 1) : undefined}>
                      <span className="option__key" aria-hidden="true">{i + 1}</span>
                      <span className="option__text">Keep the block. It can wait (costs political capital)</span>
                      <HoldCost pc={ev.raid?.holdCost.pc ?? 0} />
                    </button>
                  ) : (
                    <button ref={i === 0 ? first : undefined} type="button" className="option" disabled={busy}
                      onClick={() => pick(c.o)} aria-keyshortcuts={shortcuts && i < 9 ? String(i + 1) : undefined}>
                      <span className="option__key" aria-hidden="true">{i + 1}</span>
                      <span className="option__text">
                        <Parts parts={c.o.textParts} text={c.o.text} passive />
                        {c.o.tones?.length ? <span className="option__then"> Then choose how to put it.</span> : null}
                      </span>
                      <CostPreview cost={c.o.cost} />
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
        <div className="conversation__footer">
          <button type="button" className="btn btn--quiet" onClick={onBack}>Back to the office <kbd>Esc</kbd></button>
        </div>
      </div>
    </section>
  );
}
